import {copyText} from '@github-ui/copy-to-clipboard'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {CopyIcon, DotFillIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import {getLanguageInfo} from '../utils/language-info'
import styles from './ChatMessage.module.css'

export const reactReplaceElementPrefix = `react-replace-${Math.floor(Math.random() * 1000000000)}`

export interface ReactReplaceBlock {
  regex: RegExp
  renderer: (match: string) => JSX.Element
}

export interface ReactReplaceMatch {
  id: string
  componentType: string
  matchedContent: string
}

export interface ReactMarkdownRendererProps {
  body: string | undefined
  matches: ReactReplaceMatch[]
  onLinkClick?: (event: MouseEvent) => void
  reactReplaceBlocks: {[key: string]: ReactReplaceBlock}
}

export function ReactMarkdownRenderer(props: ReactMarkdownRendererProps): JSX.Element {
  const {body, matches, onLinkClick, reactReplaceBlocks} = props
  const headerTemplateRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLElement>()
  const [rendered, setRendered] = useState<boolean>(false)

  useEffect(() => {
    setRendered(true)
  }, [])

  useLayoutEffect(() => {
    if (containerRef.current && headerTemplateRef.current) {
      createCodeHeaders(containerRef.current, headerTemplateRef.current)
    }
  })

  const markdownViewer = useMemo(() => {
    return (
      <MarkdownViewer containerRef={containerRef} onLinkClick={onLinkClick} verifiedHTML={body as SafeHTMLString} />
    )
  }, [body, onLinkClick])

  return (
    <>
      <CodeHeaderTemplate ref={headerTemplateRef} />
      {markdownViewer}
      {rendered &&
        matches.map(match => {
          const domNode = document.getElementById(match.id)
          if (domNode && reactReplaceBlocks[match.componentType]) {
            return createPortal(reactReplaceBlocks[match.componentType]?.renderer(match.matchedContent), domNode)
          }
          return null
        })}
    </>
  )
}

/**
 * The markdown renderer puts a .snippet-header span above code blocks, and this function replaces that with a pretty header.
 */
function createCodeHeaders(container: HTMLElement, headerTemplate: HTMLDivElement) {
  for (const header of container.querySelectorAll(`.${styles['snippet-header']}`)) {
    if (header.getAttribute('data-decorated') === 'true') {
      continue
    }
    header.setAttribute('data-decorated', 'true')
    const language = header.textContent
    header.replaceChildren(...headerTemplate.cloneNode(true).childNodes)
    const languageSpan = header.querySelector(`.${styles['language-name']}`)
    if (language && languageSpan) {
      const {color, name: displayName} = getLanguageInfo(language)
      languageSpan.textContent = displayName
      const dot = header.querySelector<HTMLElement>('.language-dot')
      if (color && dot) {
        dot.style.color = color
      }
    }
    const copyButton = header.querySelector('button')
    if (copyButton) {
      copyButton.addEventListener('click', onCopyClick)
    }
  }
}

function onCopyClick(event: MouseEvent) {
  const button = event.currentTarget as HTMLButtonElement | null
  const header = button?.closest(`.${styles['snippet-header']}`)
  const content = header?.getAttribute('data-clipboard-copy-content')
  if (content) {
    void copyText(content)
  }
}

/**
 * This creates a blank code header that we clone for the actual headers for code blocks.
 */
const CodeHeaderTemplate = forwardRef<HTMLDivElement>(function CodeHeaderTemplateInner(_props, ref) {
  return (
    // I know you're thinking it would be cool to use a <template> element, but react can't render those.
    // when we clone this, we take only the children of the top level element.
    <div ref={ref} className="d-none">
      <div className={styles['snippet-header-inner']}>
        <DotFillIcon className="language-dot" />
        <span className={styles['language-name']}>Code</span>
        {/* adding an event listener here won't work because we clone these elements before they're displayed */}
        <Button variant="invisible" className="f6">
          <CopyIcon /> Copy code
        </Button>
      </div>
    </div>
  )
})

export function replaceStringsWithReactContainers(
  blocks: {[key: string]: ReactReplaceBlock},
  messageId: string,
  input?: string,
): {output: string; matches: ReactReplaceMatch[]} {
  if (!input) return {output: '', matches: []}
  const matches: ReactReplaceMatch[] = []
  let output = input
  for (const [key, block] of Object.entries(blocks)) {
    let match
    while ((match = block.regex.exec(output)) !== null) {
      const id = `${reactReplaceElementPrefix}-${matches.length}-${messageId}`
      output = output.replace(
        match[0],
        // eslint-disable-next-line github/unescaped-html-literal
        `<span id="${id}"></span>`,
      )
      matches.push({id, componentType: key, matchedContent: match[0]})
    }
  }
  return {output, matches}
}

try{ ReactMarkdownRenderer.displayName ||= 'ReactMarkdownRenderer' } catch {}
try{ CodeHeaderTemplate.displayName ||= 'CodeHeaderTemplate' } catch {}